import { ToolResult } from '../../types/tool.types';
import { ToolContext } from '../tool-context.type';

const VALID_TIME_PRESETS = ['realtime', 'sunrise', 'morning', 'afternoon', 'evening', 'sunset', 'night'] as const;
type TimePreset = typeof VALID_TIME_PRESETS[number];

const isValidTimePreset = (value: unknown): value is TimePreset => {
	return typeof value === 'string' && VALID_TIME_PRESETS.includes(value as TimePreset);
};

export const setTimeOfDay = async (args: any, context: ToolContext): Promise<ToolResult> => {
	const { threeInstance, publish } = context;

	// Log args for debugging
	// console.log('set_time_of_day called with args:', JSON.stringify(args));

	const { time, date } = args;

	// FIRST: Check if time is a valid preset (most common case)
	if (isValidTimePreset(time)) {
		if (!threeInstance) {
			return { success: false, error: 'Three.js instance not available' };
		}

		if (threeInstance.setGeoTime) {
			threeInstance.setGeoTime(time);
		} else {
			return { success: false, error: 'Geographic lighting not available in this version' };
		}

		publish('geo_lighting', 'time_changed', {
			ref: 'time_of_day',
			old: null,
			new: { time, preset: true }
		});

		return {
			success: true,
			data: { time, preset: true }
		};
	}

	// SECOND: Check if a manual date was provided
	if (typeof date === 'string' && date.trim().length > 0) {
		const parsedDate = new Date(date);
		if (isNaN(parsedDate.getTime())) {
			return {
				success: false,
				error: `Invalid date format "${date}". Use ISO date string (e.g., "2025-06-21T14:30:00")`
			};
		}

		if (!threeInstance) {
			return { success: false, error: 'Three.js instance not available' };
		}

		if (threeInstance.setGeoTime) {
			threeInstance.setGeoTime(parsedDate);
		} else {
			return { success: false, error: 'Geographic lighting not available in this version' };
		}

		publish('geo_lighting', 'time_changed', {
			ref: 'time_of_day',
			old: null,
			new: { date, manual: true }
		});

		return {
			success: true,
			data: { date, manual: true }
		};
	}

	// Neither valid preset nor valid date
	return {
		success: false,
		error: `Invalid time parameter "${time}". Must be one of: ${VALID_TIME_PRESETS.join(', ')}, or provide a valid date string.`
	};
};
